import type { AnalyticsConfig } from '../interfaces';
import { debugLog } from './debug';

const GA_CLIENT_COOKIE = '_ga';
const GOOGLE_ADS_CLICK_COOKIE = '_gcl_aw';

export type GoogleParams = {
  measurementId: string;
  clientId: string;
  sessionId: string;
  ts: string;
};

export async function getGoogleSessionParams(analytics: AnalyticsConfig) {
  debugLog(`Trying Getting Params`);
  if (window.google_tag_manager || window.google_tag_data) {
    debugLog(`Found Google Window Object:  window.google_tag_data`);
    const measurementId =
      analytics.gaMeasurementId ||
      Object.keys(window.google_tag_manager || {}).find(i => i.startsWith('G-'));
    debugLog(`Found MeasurementId`, measurementId);

    if (measurementId) {
      if (!window.dataLayer) window.dataLayer = [];

      if (!window.gtag) {
        window.gtag = function() {
          window.dataLayer.push(arguments);
        };
      }

      if (window.gtag) {
        debugLog(`Found Google Window Object:  window.gtag`);
        try {
          const [clientId, sessionId] = await Promise.all([
            getGtagValue(measurementId, 'client_id'),
            getGtagValue(measurementId, 'session_id')
          ]);

          if (clientId && sessionId) {
            const googleParams: GoogleParams = {
              measurementId,
              clientId,
              sessionId,
              ts: Date.now().toString()
            };
            debugLog(`Generated Google Params`, googleParams);
            return googleParams;
          }
        } catch (error) {
          window.GLAppInsights.trackException({
            exception: new Error(`Failed to retrieve Google session parameters: ${error}`)
          });

          return {};
        }
      }
    }
  }
  return {};
}

const getGtagValue = async (
  measurementId: string,
  fieldName: string
): Promise<string | undefined> => {
  return new Promise(resolve => {
    window.gtag('get', measurementId, fieldName, resolve);
  });
};

/**
 * Asks the host page's Google tag for a `_gl` to `url`, built from the visitor's current Google
 * cookies (GA4 ids and Google Ads clicks such as `_gcl_aw`). Google only adds `_gl` to links,
 * from document-level mousedown/keyup listeners, so a throwaway link gets a synthetic keyup
 * (unlike mousedown, it won't trip the page's close-on-outside-click handlers). Undefined when
 * the site's Google tag doesn't link to the booking domain.
 */
function createLinkerParam(url: string): string | undefined {
  if (!window.google_tag_data?.gl?.decorators?.length || !document.body) return undefined;

  const link = document.createElement('a');
  link.href = url;
  link.hidden = true;
  document.body.appendChild(link);

  try {
    link.dispatchEvent(new KeyboardEvent('keyup', { bubbles: true }));
    const decorated = new URL(link.href);
    return (
      decorated.searchParams.get('_gl') ||
      new URLSearchParams(decorated.hash.slice(1)).get('_gl') ||
      undefined
    );
  } catch {
    return undefined;
  } finally {
    link.remove();
  }
}

/** The still-encoded cookies in a `1*<hash>*<cookie>*<value>*...` linker. */
function getLinkerCookies(linker: string | null | undefined): Record<string, string> {
  const parts = linker?.split('*').slice(2) ?? [];
  const cookies: Record<string, string> = {};
  for (let index = 0; index + 1 < parts.length; index += 2) {
    cookies[parts[index]] = parts[index + 1];
  }
  return cookies;
}

/** The cookie names a linker carries, without their values, which change every minute. */
export function getLinkerCookieNames(linker: string | null | undefined): string[] {
  return Object.keys(getLinkerCookies(linker));
}

/** The click id in a linker's `_gcl_aw`: Google's web-safe base64 of `GCL.<seconds>.<gclid>`. */
function getLinkerGclid(linker: string | undefined): string | undefined {
  const value = getLinkerCookies(linker)[GOOGLE_ADS_CLICK_COOKIE];
  if (!value) return undefined;

  try {
    const decoded = atob(value.replace(/-/g, '+').replace(/_/g, '/').replace(/\./g, '='));
    return /^GCL\.\d+\.([\w-]+)/.exec(decoded)?.[1];
  } catch {
    return undefined;
  }
}

/**
 * The page params to forward, keeping the visitor's Google Ads click: a fresh `_gl` from the
 * host's Google tag wins over one already on the page URL.
 */
export function withGoogleAdsClick(
  url: string,
  pageParams: Record<string, unknown>,
  hasGaHandover: boolean
): Record<string, unknown> {
  const { _gl: pageLinker, ...params } = pageParams;
  // The booking engine lets any `_gl` replace the explicit GA4 handover, so with that handover
  // only forward a fresh linker carrying GA4 ids, else just its ad click. The page's own `_gl`
  // has usually expired by now.
  const linker =
    createLinkerParam(url) ||
    (!hasGaHandover && typeof pageLinker === 'string' ? pageLinker : undefined);

  if (linker && (!hasGaHandover || GA_CLIENT_COOKIE in getLinkerCookies(linker))) {
    params._gl = linker;
  } else if (!params.gclid) {
    const gclid = getLinkerGclid(linker);
    if (gclid) params.gclid = gclid;
  }

  return params;
}
